export const HR_TEAM_ROUTE_ROLES = [
  'hr_admin',
  'admin_hr',
  'hr',
  'human_resource',
  'human_resources',
  'hr_team',
  'hr_officer',
  'hr_supervisor',
  'hr_manager',
  'hr_generalist',
  'people_operations',
  'people_ops',
  'recruiter',
  'talent_acquisition',
];

export const HOD_ROUTE_ROLES = [
  'hod',
  'head_of_department',
  'head_department',
  'head_of_dept',
];

export const OPERATIONS_ROUTE_ROLES = [
  'operation_manager',
  'operations_manager',
  'operations',
  'operation',
];

export const EXECUTIVE_ROUTE_ROLES = [
  'executive',
  'executive_manager',
  'executive_director',
  'executive_managing_director',
];

export const REQUEST_WORKFLOW_ROUTE_ROLES = [
  'employee',
  'finance_coordinator',
  'manager',
  'ceo',
  'finance',
  ...HR_TEAM_ROUTE_ROLES,
  ...HOD_ROUTE_ROLES,
  ...OPERATIONS_ROUTE_ROLES,
  ...EXECUTIVE_ROUTE_ROLES,
];

export const PROFILE_ROUTE_ROLES = Array.from(new Set([
  ...REQUEST_WORKFLOW_ROUTE_ROLES,
  'platform_admin',
  'super_admin',
]));

export const PERFORMANCE_ROUTE_ROLES = Array.from(new Set([
  ...REQUEST_WORKFLOW_ROUTE_ROLES,
  'recruiter',
]));

export const normalizeRoleKey = (value) => {
  const role = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  if (role.endsWith('_employee')) return 'employee';
  if (role === 'hr_admin' || role === 'admin_hr') return 'hr_admin';
  if (role === 'recruiter' || role === 'talent_acquisition') return 'recruiter';
  if (HOD_ROUTE_ROLES.includes(role)) return 'hod';
  if (OPERATIONS_ROUTE_ROLES.includes(role)) return 'operation_manager';
  if (EXECUTIVE_ROUTE_ROLES.includes(role)) return role === 'executive_managing_director' ? 'executive' : role;
  if (HR_TEAM_ROUTE_ROLES.includes(role) && role !== 'hr_admin' && role !== 'recruiter') return 'hr_team';

  return role;
};

// Maps any role string to the API route prefix the Laravel backend expects.
// Backend groups (see routes/api.php):
//   /admin                -> role:hr_admin
//   /manager              -> role:manager|head_of_department|operation_manager|executive
//   /ceo                  -> role:ceo|executive|executive_manager|executive_director|executive_managing_director
//   /finance              -> role:finance
//   /finance_coordinator  -> role:finance_coordinator
//   /employee             -> role:employee
export const getRoleApiPrefix = (role) => {
  const normalized = normalizeRoleKey(role);
  if (normalized === 'hr_admin' || normalized === 'hr_coordinator') return '/admin';
  if (normalized === 'finance') return '/finance';
  if (normalized === 'finance_coordinator') return '/finance_coordinator';
  if (normalized === 'ceo') return '/ceo';
  if (
    normalized === 'manager' ||
    normalized === 'hod' ||
    normalized === 'operation_manager' ||
    EXECUTIVE_ROUTE_ROLES.includes(normalized)
  ) {
    return '/manager';
  }
  return '/employee';
};

export const parseStoredList = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed.map((item) => String(item || '').trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
};

const unique = (items) => Array.from(new Set(items.filter(Boolean)));

// WEB-314: Aliases must NOT escalate privilege. The previous `hr_team` group used
// the full HR_TEAM_ROUTE_ROLES list, which itself contained 'hr_admin' and
// 'admin_hr', so an employee whose actual_role was 'hr_team' was implicitly
// granted hr_admin access by ProtectedRoute. Strip the admin tokens out so
// hr_team only aliases to hr_team-level roles.
const HR_TEAM_ALIAS_ROLES = HR_TEAM_ROUTE_ROLES.filter(
  (role) => role !== 'hr_admin' && role !== 'admin_hr'
);

const roleAliasGroups = {
  hr_team: HR_TEAM_ALIAS_ROLES,
  recruiter: ['recruiter', 'talent_acquisition'],
  hod: HOD_ROUTE_ROLES,
  operation_manager: OPERATIONS_ROUTE_ROLES,
  executive: EXECUTIVE_ROUTE_ROLES,
  manager: ['manager', ...HOD_ROUTE_ROLES, ...OPERATIONS_ROUTE_ROLES],
  ceo: ['ceo', ...EXECUTIVE_ROUTE_ROLES],
  finance: ['finance'],
  finance_coordinator: ['finance_coordinator'],
  employee: ['employee'],
  hr_admin: ['hr_admin', 'admin_hr'],
};

export const roleAliasesFor = (role) => {
  const normalizedRole = normalizeRoleKey(role);
  return roleAliasGroups[normalizedRole] || [normalizedRole];
};

export const expandAllowedRoles = (roles = []) =>
  unique(
    roles.flatMap((role) => {
      const normalizedRole = normalizeRoleKey(role);
      return roleAliasesFor(normalizedRole);
    }).map(normalizeRoleKey)
  );

export const hasRoleAlias = (permissions = [], roleKey) => {
  const normalizedRole = normalizeRoleKey(roleKey);
  const normalizedPermissions = permissions.map((permission) => String(permission || '').trim().toLowerCase());
  return normalizedPermissions.includes('*') || normalizedPermissions.includes(`role.${normalizedRole}`);
};

export const collectRoleCandidates = ({ role, actualRole, permissions = [] } = {}) => {
  const directRoles = [role, actualRole].map(normalizeRoleKey).filter(Boolean);
  const permissionRoles = permissions
    .map((permission) => String(permission || '').trim().toLowerCase())
    .filter((permission) => permission.startsWith('role.'))
    .map((permission) => normalizeRoleKey(permission.slice(5)))
    .filter(Boolean);

  return unique([...directRoles, ...permissionRoles]);
};

export const roleMatchesAllowed = ({ role, actualRole, permissions = [], allowedRoles = [], isPlatformAdmin = false } = {}) => {
  if (isPlatformAdmin) return true;
  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true;
  if (permissions.map((permission) => String(permission || '').trim()).includes('*')) return true;

  const expandedAllowedRoles = expandAllowedRoles(allowedRoles);
  const roleCandidates = collectRoleCandidates({ role, actualRole, permissions })
    .flatMap((candidate) => roleAliasesFor(candidate).map(normalizeRoleKey));

  return roleCandidates.some((candidate) => expandedAllowedRoles.includes(candidate));
};

// Canonical, human-readable display label for a role string. Handles raw
// values from the backend (`hr_admin`), values that lost their separator
// somewhere in the pipeline (`hrmanager`), and unknown values without
// dropping data.
const ROLE_DISPLAY_OVERRIDES = {
  hr_admin: 'HR Admin',
  admin_hr: 'HR Admin',
  hr_team: 'HR Team',
  hr_manager: 'HR Manager',
  hr_officer: 'HR Officer',
  hr_supervisor: 'HR Supervisor',
  hr_generalist: 'HR Generalist',
  hr_partner: 'HR Partner',
  hr: 'HR',
  hod: 'HOD',
  head_of_department: 'Head of Department',
  head_of_dept: 'Head of Department',
  head_department: 'Head of Department',
  ceo: 'CEO',
  executive: 'Executive',
  executive_manager: 'Executive Manager',
  executive_director: 'Executive Director',
  executive_managing_director: 'Executive MD',
  manager: 'Manager',
  operation_manager: 'Operations Manager',
  operations_manager: 'Operations Manager',
  operations: 'Operations',
  operation: 'Operations',
  employee: 'Employee',
  finance: 'Finance',
  finance_coordinator: 'Finance Coordinator',
  recruiter: 'Recruiter',
  talent_acquisition: 'Talent Acquisition',
  people_partner: 'People Partner',
  people_operations: 'People Operations',
  people_ops: 'People Ops',
  platform_admin: 'Platform Admin',
  super_admin: 'Super Admin',
};

export const formatRoleLabel = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return 'Employee';

  const normalized = String(raw).toLowerCase().replace(/[\s-]+/g, '_');
  if (ROLE_DISPLAY_OVERRIDES[normalized]) return ROLE_DISPLAY_OVERRIDES[normalized];

  // Some upstream code concatenates words (e.g. "hrmanager"). Try to detect
  // and split well-known prefixes before falling back to plain title casing.
  if (/^hr[a-z]/.test(normalized)) {
    const rest = normalized.slice(2);
    if (ROLE_DISPLAY_OVERRIDES[`hr_${rest}`]) return ROLE_DISPLAY_OVERRIDES[`hr_${rest}`];
    return `HR ${rest.charAt(0).toUpperCase() + rest.slice(1)}`;
  }

  return normalized
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
    .join(' ')
    .trim();
};

const titleCaseRole = (value) => formatRoleLabel(value);

export const createRoleCapabilities = ({
  role = 'employee',
  actualRole = '',
  permissions = [],
  isPlatformAdmin = false,
  userName = '',
  employeeCode = '',
} = {}) => {
  const normalizedRole = normalizeRoleKey(role || 'employee');
  const normalizedActualRole = normalizeRoleKey(actualRole);
  const candidates = collectRoleCandidates({ role: normalizedRole, actualRole: normalizedActualRole, permissions });
  const hasAny = (...roleKeys) => roleKeys.some((roleKey) => {
    const aliases = roleAliasesFor(roleKey).map(normalizeRoleKey);
    return candidates.some((candidate) => aliases.includes(candidate)) || hasRoleAlias(permissions, roleKey);
  });

  const isSuperAdmin = Boolean(isPlatformAdmin);
  const hasEmployeeAccess = hasAny('employee');
  const hasFinanceCoordinatorAccess = hasAny('finance_coordinator');
  const hasHrAdminAccess = hasAny('hr_admin');
  const hasHrTeamAccess = hasHrAdminAccess || hasAny('hr_team');
  const hasRecruiterAccess = hasAny('recruiter');
  const hasHodAccess = hasAny('hod');
  const hasOperationsAccess = hasAny('operation_manager');
  const hasExecutiveAccess = hasAny('executive');
  const hasManagerAccess = hasAny('manager');
  const hasFinanceAccess = hasAny('finance');
  const hasCeoAccess = hasAny('ceo');
  const hasManagerFamilyAccess =
    hasManagerAccess || hasHodAccess || hasOperationsAccess || hasFinanceAccess || hasCeoAccess || hasExecutiveAccess;
  const hasExecutiveFamilyAccess = hasCeoAccess || hasExecutiveAccess;
  const hasRequestActionAccess =
    hasManagerFamilyAccess || hasHrTeamAccess || hasFinanceCoordinatorAccess;
  const hasAppraisalAccess = hasRequestActionAccess || hasRecruiterAccess || hasEmployeeAccess;
  const hasPreApprovalAccess = hasHrAdminAccess || hasHodAccess || hasOperationsAccess || hasCeoAccess || hasExecutiveAccess;

  return {
    role: normalizedRole,
    actualRole: normalizedActualRole,
    permissions,
    userName,
    employeeCode: String(employeeCode || '').trim(),
    isPlatformAdmin: Boolean(isPlatformAdmin),
    isSuperAdmin,
    roleLabel: isSuperAdmin ? 'Super Admin' : titleCaseRole(normalizedRole),
    hasEmployeeAccess,
    hasFinanceCoordinatorAccess,
    hasHrAdminAccess,
    hasHrTeamAccess,
    hasRecruiterAccess,
    hasManagerAccess,
    hasHodAccess,
    hasOperationsAccess,
    hasFinanceAccess,
    hasCeoAccess,
    hasExecutiveAccess,
    hasManagerFamilyAccess,
    hasExecutiveFamilyAccess,
    hasRequestActionAccess,
    hasAppraisalAccess,
    hasPreApprovalAccess,
  };
};
